import { useState } from "react";
import { Mail, Pencil, ShieldAlert, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useUserMe } from "@/lib/user-me";
import { useCancelEmailChange, usePendingEmailChange } from "@/lib/auth-me";
import { useToast } from "@/components/v2/Toast";
import { ChangeEmailDialog } from "@/components/v2/account/ChangeEmailDialog";
import { SettingsSection } from "@/components/v2/account/SettingsSection";

/**
 * `/account/email` — current email + Linear-style change flow. The
 * verification link goes to the NEW address; the old email keeps
 * working until the user clicks it. While a change is in flight we
 * show a banner with a Cancel button.
 */
export function AccountEmail() {
  const me = useUserMe();
  const pending = usePendingEmailChange();
  const cancel = useCancelEmailChange();
  const toast = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);

  const data = pending.data;

  return (
    <SettingsSection
      title="Email"
      description="The email you use to sign in. Changes require verification on the new address."
    >
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <Mail className="h-4 w-4 text-muted-foreground" />
          <p className="text-sm">{me.data?.email ?? "—"}</p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setDialogOpen(true)}
            className="ml-auto"
          >
            <Pencil className="h-3.5 w-3.5 mr-1.5" />
            Change email
          </Button>
        </div>

        {data && (
          <div className="rounded-md border border-status-pending/30 bg-status-pending/5 px-3 py-2.5 text-sm flex items-start gap-2">
            <ShieldAlert className="h-4 w-4 text-status-pending shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p>
                We sent a verification link to <strong>{data.newEmail}</strong>.
                Click it to finish the change. Your current address keeps
                working until you do.
              </p>
              <button
                type="button"
                onClick={() =>
                  cancel.mutate(undefined, {
                    onSuccess: () => toast.success("Pending change cancelled"),
                    onError: (e) =>
                      toast.error("Couldn't cancel", (e as Error).message),
                  })
                }
                className="text-xs text-primary hover:underline mt-1 inline-flex items-center gap-1"
              >
                <X className="h-3 w-3" /> Cancel pending change
              </button>
            </div>
          </div>
        )}
      </div>

      <ChangeEmailDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        currentEmail={me.data?.email ?? ""}
      />
    </SettingsSection>
  );
}
